import { call } from "hb/insights/use";
import { useQuery } from "hb/insights/use_query";
import { useOutlets } from "hb/insights/use_outlets";
import { useListen } from "hb/insights/use_listen";
import URL from "hb/lib/url";
import { incrementedIds } from "hb/lib/collection_helpers";
import { isEmpty } from "hb/lib/utils";

import { DEFAULT_QUERY } from "hb/insights/query";
import QueryMeta from "hb/insights/query_meta";
import QueryResult from "hb/insights/query_result";

import ApplicationController from "../../application_component_controller";

export default class extends ApplicationController {
  static targets = ["error", "chart", "chartWrap"];

  // If a view is swappable, then it must have a static `viewId` variable
  static viewId = null;

  static get values() {
    return {
      hideConfig: Boolean,
      startAt: String,
      endAt: String,
      colorMap: {
        type: Object,
        default: {
          orange: "#F4692C",
          blue: "#4e79a7",
          red: "#e15759",
          teal: "#00A6A1",
          green: "#59a14f",
          yellow: "#edc949",
          purple: "#af7aa1",
          pink: "#FFCBBB",
          brown: "#9c755f",
          gray: "#bab0ab",
          black: "#000000",
          white: "#ffffff",
          cyan: "#00ffff",
          olive: "#808000",
          gold: "#FFD700",
          silver: "#C0C0C0",
          bronze: "#CD7F32",
        },
      },
      syncId: String,
      useParamState: { Boolean, default: true },
      querySessionId: String,
      widgetId: String,
    };
  }

  connect() {
    useListen(this);

    this.comboboxItemTemplate = this.compileSelector(
      '[data-template="comboboxItem"]',
    );
    this.fieldGroupItemTemplate = this.compileSelector(
      '[data-template="fieldGroupItem"]',
    );

    this.colorValues = Object.values(this.colorMapValue);
    this.errorMessage = null;
    this.derivedConfig = {};
    this.enabledGroups = new Set();

    useOutlets(this, {
      components: ["chart", "legend"],
      scope: this.element,
    });
  }

  get widgetConfig() {
    return {
      queryId: this.querySessionIdValue,
      query: this.widgetQueryValue,
    };
  }

  outletsConnected() {
    if (this.outletComponent("legend")) {
      this.listen(
        "legendHover",
        this.legendHover,
        this.outletComponent("legend"),
      );
      this.listen(
        "legendSelected",
        this.legendSelected,
        this.outletComponent("legend"),
      );
    }

    if (this.outletComponent("chart")) {
      this.listen("visibilitychange", this.handleVisibilityChange, document);
    }

    this.listenWindow("updateVisState", this.onUpdateVisState);
  }

  // I observed that, in chrome, the chart would dissappear when the tab was
  // hidden and then shown again. To fix this, we resize the chart when the
  // tab is visible again.
  handleVisibilityChange() {
    if (document.visibilityState === "visible") {
      this.outletComponent("chart").resize();
    }
  }

  initQuery() {
    if (this.hasSchemaValue && this.hasResultsValue) {
      this.buildLocalQuery();
    } else {
      useQuery(this, { sessionId: this.querySessionIdValue });
    }
  }

  // Validates that the name of the fields in the config are present in the schema
  hasUnknownFields(...fields) {
    return !fields.every((f) =>
      f === "" || f === undefined ? true : this.query.meta.has(f),
    );
  }

  buildLocalQuery() {
    const meta = new QueryMeta({
      startAt: this.startAtValue,
      endAt: this.endAtValue,
      schema: this.schemaValue,
      fields: this.schemaValue.map((v) => v.name),
    });
    this.query = {
      meta,
      results: this.resultsValue.map((res) => new QueryResult(res, meta)),
    };

    call(this, "setConfig");
    call(this, "processConfig");
    if (!this.hideConfigValue) call(this, "renderConfig");
    this._validate();
    this._render();
  }

  onQueryReady({
    detail: {
      query: { data },
    },
  }) {
    this.errorMessage = null;
    this.query = data;

    if (isEmpty(this.config)) {
      call(this, "setConfig");
    }
    call(this, "processConfig");
    if (!this.hideConfigValue) call(this, "renderConfig");
    this._validate();
    this._render();
  }

  loadEmptyQuery() {
    URL.updateParam("query", DEFAULT_QUERY);
  }

  onQueryEmpty() {
    this.outletComponent("chart")?.showEmptyQuery();
  }

  _validate() {
    let { results } = this.query;
    this.hideConfigMessage = false;

    if (results?.length == 0) {
      this.errorMessage = "No Results";
      this.hideConfigMessage = true;
    } else {
      call(this, "validate");
    }
  }

  _render() {
    if (this.errorMessage !== null) {
      this.outletComponent("chart")?.showError({
        message: this.errorMessage,
        showConfigMessage: !this.hideConfigMessage,
      });
    } else {
      this.outletComponent("chart")?.hideError();
      call(this, "render");
    }
  }

  get defaultChartConfig() {
    let config = {
      legend: null,
      grid: {
        borderWidth: 0,
        left: 20,
        bottom: 0,
        right: 20,
        top: 10,
        show: true,
        containLabel: true,
      },
      backgroundColor: "transparent",

      // Tableau 10
      color: [
        "#F4692C",
        "#4e79a7",
        "#e15759",
        "#00A6A1",
        "#59a14f",
        "#edc949",
        "#af7aa1",
        "FFCBBB",
        "#9c755f",
        "#bab0ab",
      ],

      xAxis: {
        axisLabel: {
          show: true,
          formatter: {
            year: "{boldStyle|{yyyy}}",
            month: "{boldStyle|{MMM}}",
            day: "{boldStyle|{MMM} {d}}",
            hour: "{HH}:{mm}",
            minute: "{HH}:{mm}",
            second: "{HH}:{mm}:{ss}",
            millisecond: "{hh}:{mm}:{ss} {SSS}",
            none: "{yyyy}-{MM}-{dd} {hh}:{mm}:{ss} {SSS}",
          },
          rich: {
            boldStyle: {
              fontWeight: "bold",
            },
          },
        },
      },
      textStyle: {
        fontFamily: `system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", "Noto Sans", "Liberation Sans", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`,
      },
      tooltip: {
        confine: true,
        appendToBody: true,
        order: "valueDesc",
      },
    };

    return config;
  }

  get defaultYAxis() {
    return {
      nameTextStyle: { align: "left" },
    };
  }

  renderLegend(config) {
    this.outletComponent("legend")?.render(config);
  }

  renderFieldGroups(config) {
    const fieldGroups = this.find(".fieldGroups");
    if (!fieldGroups) return;

    fieldGroups.innerHTML = config
      ?.map(({ id, name, color, colorSet, axis }) =>
        this.fieldGroupItemTemplate({ id, name, color, colorSet, axis }),
      )
      .join("\n");
  }

  groupDisabled(index) {
    if (this.enabledGroups.size === 0) return false;
    return !this.enabledGroups.has(index.toString());
  }

  onUpdateVisState(visState) {
    if (visState.querySessionId !== this.querySessionIdValue) return;

    call(this, "setConfig", visState);
    call(this, "processConfig");
    call(this, "renderConfig");

    this._validate();
    this._render();
  }

  inputUpdate({ detail: { name, value } }) {
    this.errorMessage = null;

    if (value === "__empty") {
      value = "";
    }

    this.emitWindow("vis:paramChange", {
      querySessionId: this.querySessionIdValue,
      name,
      value,
    });
  }

  emitVisConfigUpdate() {
    if (this.hasWidgetIdValue) {
      const eventData = {
        widgetId: this.widgetIdValue,
        vis: {
          type: this.constructor.viewId,
          chart_config: this.config,
        },
      };

      this.emitWindow("queryConfig:update", eventData);
    }
  }

  legendHover({ entered, seriesIndex }) {
    this.outletComponent("chart")?.chart?.dispatchAction({
      type: entered ? "highlight" : "downplay",
      seriesIndex,
    });
  }

  legendSelected({ shiftKey, seriesIndex }) {
    const groupLength = this.derivedConfig.groupConfig.length;
    if (shiftKey && this.enabledGroups.size === 0) {
      this.enabledGroups = new Set(
        incrementedIds(groupLength, (i) => i.toString()),
      );
      this.enabledGroups.delete(seriesIndex);
    } else if (shiftKey) {
      this.enabledGroups.has(seriesIndex)
        ? this.enabledGroups.delete(seriesIndex)
        : this.enabledGroups.add(seriesIndex);
    } else if (
      this.enabledGroups.has(seriesIndex) &&
      this.enabledGroups.size !== groupLength
    ) {
      this.enabledGroups.clear();
    } else {
      this.enabledGroups = new Set([seriesIndex]);
    }

    call(this, "processConfig");
    call(this, "validate");
    this._render();
  }
}
